/**
 * The one way the Console talks to its API (docs/auth/console-identity-and-access.md
 * sections 6.4 and 7). Same origin, /api, so there is no CORS and the session
 * cookie is sent only where the server asks for it.
 *
 *   - Every call carries X-Lfoc-Request: 1 - the header the cookie routes
 *     require, which no cross-site form can send.
 *   - An authenticated call carries the access token as a Bearer header. The
 *     token lives in memory (auth/session.js); the refresh token never reaches
 *     the browser.
 *   - A 401 is an expired or refused token: the session refreshes once (the
 *     cookie route) and the call is retried once - unless its code is a
 *     step-up's (STEP_UP_CODE_INVALID, a wrong authenticator code): the
 *     session is fine, and resending would spend the used challenge again and
 *     come back STEP_UP_CHALLENGE_INVALID, losing the "not right" answer.
 *   - A 403 whose code is SESSION_IDLE, SESSION_EXPIRED, SESSION_REVOKED or
 *     SESSION_UNKNOWN means the server has ended the session: the Console
 *     signs out and says why. Any other refusal is thrown to the screen.
 *   - Errors are the API's envelope {error: {code, message, details}}, thrown as
 *     ApiError - a screen shows the message and branches on the code.
 *   - A refusal with no envelope never reached the Console API: every refusal
 *     of its routes and its authorizer carries one. A bare 403 that says no
 *     more than "Forbidden" is the firewall's, and a bare 413 API Gateway's
 *     size limit; each is given a code and words a person can act on
 *     (bareRefusal).
 */

export const SESSION_ENDED_CODES = Object.freeze(['SESSION_IDLE', 'SESSION_EXPIRED', 'SESSION_REVOKED', 'SESSION_UNKNOWN']);

/** A step-up's own refusal (authorise.js): about the code or the challenge, never the session. */
const isStepUpCode = (code) => typeof code === 'string' && code.startsWith('STEP_UP_');

/**
 * The web ACLs refuse a request before the Console API sees it: a body over
 * 8 KB (AWS's SizeRestrictions_BODY), or a burst past a rate limit
 * (62-console-api-waf.yaml, and the edge's 60-waf-cloudfront.yaml). The
 * stage's WAF_FILTERED gateway response (services/console-api/serverless.yml)
 * puts that refusal in the envelope - this code, these words. The edge's
 * block, and the stage's own until that response is deployed, is a bare 403:
 * API Gateway's {"message": "Forbidden"}, or a page that is not JSON at all.
 * That is given the same code and the same words, so a firewall refusal never
 * reads as a missing permission, whichever layer made it.
 */
export const FIREWALL_CODE = 'WAF_FILTERED';
export const FIREWALL_MESSAGE = 'The firewall refused this request: too large, or too many requests in a few minutes. '
  + 'Act on fewer transactions at a time, or wait and try again.';

/** API Gateway's REQUEST_TOO_LARGE: a body over its own 10 MB limit, answered 413 with no envelope. */
export const TOO_LARGE_CODE = 'REQUEST_TOO_LARGE';
export const TOO_LARGE_MESSAGE = 'The request is too large for the server. Act on fewer at a time.';

/**
 * The code and words for a refusal that carries no envelope, or null to keep
 * the default. A bare 403 with a message of its own that is not the
 * firewall's (API Gateway's "Missing Authentication Token" for a route it
 * does not have) keeps it.
 */
function bareRefusal(status, body) {
  const said = typeof body?.message === 'string' ? body.message : '';
  if (status === 403 && (said === '' || said === 'Forbidden')) return { code: FIREWALL_CODE, message: FIREWALL_MESSAGE };
  if (status === 413) return { code: TOO_LARGE_CODE, message: TOO_LARGE_MESSAGE };
  return null;
}

export class ApiError extends Error {
  constructor(message, { status = 0, code = 'NETWORK', details = null, requestId } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

async function errorFrom(response) {
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const envelope = body?.error ?? null;
  const requestId = response.headers?.get?.('x-amzn-requestid') ?? undefined;
  // API Gateway's own refusals (a WAF block, a missing route, its size limit) have no envelope.
  const bare = envelope ? null : bareRefusal(response.status, body);
  if (bare) return new ApiError(bare.message, { status: response.status, code: bare.code, details: null, requestId });
  const message = envelope?.message || body?.message || 'The request failed (' + response.status + ').';
  return new ApiError(message, { status: response.status, code: envelope?.code || 'HTTP_' + response.status, details: envelope?.details ?? null, requestId });
}

/**
 * @param {{
 *   apiBase: string,
 *   accessToken: () => Promise<string|null>,   the current token, refreshed first if about to lapse
 *   refresh: () => Promise<boolean>,           a forced refresh; false when the session is gone
 *   onSessionEnded: (code: string, message: string) => void,
 *   fetchImpl?: typeof fetch,
 * }} options
 */
export function createHttp({ apiBase, accessToken, refresh, onSessionEnded, fetchImpl = (...args) => fetch(...args) }) {
  const base = String(apiBase).replace(/\/$/, '');

  async function send(method, path, { body, auth = true, retried = false } = {}) {
    const headers = { Accept: 'application/json', 'X-Lfoc-Request': '1' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (auth) {
      const token = await accessToken();
      if (!token) {
        onSessionEnded('SESSION_UNKNOWN', 'You are not signed in.');
        throw new ApiError('You are not signed in.', { status: 401, code: 'SESSION_UNKNOWN' });
      }
      headers.Authorization = 'Bearer ' + token;
    }
    let response;
    try {
      response = await fetchImpl(base + path, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        // The session cookie is __Host- and same-origin; it rides only on the
        // routes whose path the browser matches, and the server reads it only
        // on /auth/refresh and /auth/sign-out.
        credentials: 'same-origin',
        cache: 'no-store',
      });
    } catch (error) {
      throw new ApiError('Fund Operations could not reach its server: ' + error.message);
    }
    if (response.ok) {
      if (response.status === 204) return null;
      return response.json();
    }
    // The envelope first: a 401 for a wrong step-up code is not a session to refresh.
    const error = await errorFrom(response);
    if (response.status === 401 && auth && !retried && !isStepUpCode(error.code)) {
      if (await refresh()) return send(method, path, { body, auth, retried: true });
    }
    if (auth && SESSION_ENDED_CODES.includes(error.code)) onSessionEnded(error.code, error.message);
    throw error;
  }

  return {
    get: (path, options) => send('GET', path, options),
    post: (path, body, options) => send('POST', path, { ...options, body: body ?? {} }),
    put: (path, body, options) => send('PUT', path, { ...options, body: body ?? {} }),
    patch: (path, body, options) => send('PATCH', path, { ...options, body: body ?? {} }),
  };
}
